import { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { getProductsByBranch, getProductImageUrl } from '../../../../../services/PublicOrderService';

const formatCurrency = (value) =>
    (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const StepProducts = ({ companyUuid, branchId, onNext, onBack, cart, onCartChange }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadProducts = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await getProductsByBranch(companyUuid, branchId);
            setProducts(response.data || []);
        } catch (err) {
            setError('Não foi possível carregar os produtos. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }, [companyUuid, branchId]);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    const getQty = (productId) => cart[productId]?.qty || 0;

    const addToCart = (product) => {
        const current = cart[product.id];
        onCartChange({
            ...cart,
            [product.id]: { product, qty: (current?.qty || 0) + 1 },
        });
    };

    const removeFromCart = (product) => {
        const current = cart[product.id];
        if (!current || current.qty <= 0) return;
        const newQty = current.qty - 1;
        if (newQty === 0) {
            const updated = { ...cart };
            delete updated[product.id];
            onCartChange(updated);
        } else {
            onCartChange({ ...cart, [product.id]: { product, qty: newQty } });
        }
    };

    const cartItems = Object.values(cart).filter((i) => i.qty > 0);
    const cartTotal = cartItems.reduce((s, i) => s + (i.product.price || 0) * i.qty, 0);

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Carregando produtos...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-3">
                <Alert variant="danger">
                    <i className="fa fa-exclamation-circle me-2"></i>
                    {error}
                </Alert>
                <div className="text-center">
                    <Button variant="outline-primary" onClick={loadProducts}>
                        <i className="fa fa-redo me-2"></i>Tentar novamente
                    </Button>
                </div>
            </div>
        );
    }

    if (products.length === 0) {
        return (
            <div className="text-center py-5">
                <i className="fa fa-box-open fa-3x text-muted mb-3"></i>
                <p className="text-muted">Nenhum produto disponível nesta filial.</p>
                <Button variant="outline-secondary" onClick={onBack}>
                    <i className="fa fa-arrow-left me-2"></i>Voltar
                </Button>
            </div>
        );
    }

    return (
        <div className="step-products">
            <div className="step-header mb-4">
                <h4 className="step-title">
                    <i className="fa fa-shopping-basket me-2 text-primary"></i>
                    Escolha os produtos
                </h4>
                <p className="step-subtitle text-muted">
                    Adicione os produtos desejados ao seu pedido.
                </p>
            </div>

            {/* Grid de produtos */}
            <Row className="g-3">
                {products.map((product) => {
                    const qty = getQty(product.id);
                    return (
                        <Col xs={6} md={4} key={product.id}>
                            <Card className={`product-card h-100${qty > 0 ? ' in-cart' : ''}`}>
                                {/* Imagem ou placeholder */}
                                <div className="product-img-wrap">
                                    {product.imageUrl ? (
                                        <img
                                            src={getProductImageUrl(companyUuid, product.id)}
                                            alt={product.name}
                                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                        />
                                    ) : null}
                                    <div className="product-img-placeholder" style={product.imageUrl ? { display: 'none' } : {}}>
                                        <i className="fa fa-image fa-2x"></i>
                                        <small>Sem foto</small>
                                    </div>
                                </div>

                                <Card.Body className="p-3 d-flex flex-column">
                                    <p className="product-name mb-1">{product.name}</p>
                                    {product.description && (
                                        <p className="product-description mb-2">{product.description}</p>
                                    )}
                                    <p className="product-price mb-3 mt-auto">
                                        {formatCurrency(product.price)}
                                    </p>

                                    {/* Controles de carrinho */}
                                    {qty === 0 ? (
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            className="w-100"
                                            onClick={() => addToCart(product)}
                                        >
                                            <i className="fa fa-plus me-1"></i>Adicionar
                                        </Button>
                                    ) : (
                                        <div className="cart-qty-controls justify-content-center">
                                            <button
                                                className="cart-qty-btn remove"
                                                onClick={() => removeFromCart(product)}
                                                aria-label="Remover"
                                            >
                                                −
                                            </button>
                                            <span className="cart-qty-value">{qty}</span>
                                            <button
                                                className="cart-qty-btn add"
                                                onClick={() => addToCart(product)}
                                                aria-label="Adicionar"
                                            >
                                                +
                                            </button>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            {/* Resumo do carrinho */}
            {cartItems.length > 0 && (
                <div className="order-cart-summary mt-4 p-3">
                    <h6 className="fw-bold mb-3">
                        <i className="fa fa-shopping-cart me-2 text-primary"></i>
                        Seu carrinho ({cartItems.length} {cartItems.length === 1 ? 'item' : 'itens'})
                    </h6>
                    {cartItems.map((item) => (
                        <div className="cart-item-row" key={item.product.id}>
                            <span>
                                {item.qty}× {item.product.name}
                            </span>
                            <span className="fw-semibold">
                                {formatCurrency((item.product.price || 0) * item.qty)}
                            </span>
                        </div>
                    ))}
                    <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                        <span className="order-cart-total">Total</span>
                        <span className="order-cart-total">{formatCurrency(cartTotal)}</span>
                    </div>
                </div>
            )}

            {/* Ações */}
            <div className="d-flex justify-content-between mt-4">
                <Button variant="outline-secondary" onClick={onBack}>
                    <i className="fa fa-arrow-left me-2"></i>Voltar
                </Button>
                <Button
                    variant="primary"
                    onClick={onNext}
                    disabled={cartItems.length === 0}
                >
                    Continuar<i className="fa fa-arrow-right ms-2"></i>
                </Button>
            </div>
        </div>
    );
};

export default StepProducts;
